"use client";

import { useMemo, use, useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import LayoutOfCakes from "@/features/product-listing/LayoutOfCakes";
import ShowingResultsTitle from "@/features/product-listing/ShowingResultsTitle";
import { getProductListing } from "@/services/api/product/listing";
import { useInView } from "react-intersection-observer";
interface Product {
  id: string | number;
  [key: string]: unknown;
}
interface PageData {
  results?: Product[];
  count?: number;
  [key: string]: unknown;
}
interface ProductGridProps {
  initialProductsPromise?: Promise<unknown>;
  searchParams?: {
    [key: string]: string | string[] | undefined;
  };
  searchParamsProp?: {
    [key: string]: string | string[] | undefined;
  };
  isThemeCake?: boolean;
  selectedProductType?: string;
  isCollection?: boolean;
  initialSubTypesData?: unknown;
  dietaryCategoriesData?: unknown;
  showSortingOptions?: boolean;
}
export default function ProductGrid({
  initialProductsPromise,
  searchParams,
  searchParamsProp,
  isThemeCake,
  selectedProductType,
  isCollection,
  initialSubTypesData,
  dietaryCategoriesData,
  showSortingOptions
}: ProductGridProps) {
  // Suspend the component while the initial products are fetched on the server!
  const initialProductsData = initialProductsPromise ? use(initialProductsPromise) : undefined;
  const {
    data: productsData,
    isLoading: isProductsLoading,
    isError: isProductsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchProducts
  } = useInfiniteQuery({
    queryKey: ["product-listing", searchParams.toString().split("&").sort().join("&"), isThemeCake],
    queryFn: ({
      pageParam = 1
    }) => {
      return getProductListing({
        searchParams: searchParams,
        page: pageParam,
        hasThemeCake: isThemeCake
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.next) {
        try {
          const url = new URL(lastPage.next);
          const pageParam_0 = url.searchParams.get("page");
          return pageParam_0 ? parseInt(pageParam_0) : undefined;
        } catch (e) {
          return typeof lastPage.next === "number" ? lastPage.next : undefined;
        }
      }
      return undefined;
    },
    initialPageParam: 1,
    initialData: initialProductsData ? {
      pages: [initialProductsData],
      pageParams: [1]
    } : undefined,
    initialDataUpdatedAt: initialProductsData ? Date.now() : undefined,
    staleTime: 60 * 1000,
    // 1 minute
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const allProducts = useMemo(() => {
    const products = productsData?.pages?.flatMap((page: PageData) => page.results || []) || [];
    const seenIds = new Set();
    return products.filter((product: Product) => {
      if (!product || seenIds.has(product.id)) {
        return false;
      }
      seenIds.add(product.id);
      return true;
    });
  }, [productsData]);
  const totalProductCount = productsData?.pages?.[0]?.count || 0;
  const {
    ref,
    inView
  } = useInView();
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);
  return <>
      <div className="w-full lg:space-y-6 space-y-4">
        <ShowingResultsTitle productscount={allProducts?.length} selectedCount={totalProductCount} showSort={showSortingOptions} isProductLoading={isProductsLoading && !initialProductsData} searchParams={searchParamsProp} />
        <LayoutOfCakes selectedProductType={selectedProductType} products={allProducts} isProductsLoading={isProductsLoading && !initialProductsData} isProductsError={isProductsError} refetchProducts={refetchProducts} isFetchingNextPage={isFetchingNextPage} isCollection={isCollection} isThemeCake={isThemeCake} initialSubTypesData={initialSubTypesData} dietaryCategoriesData={dietaryCategoriesData} searchParams={searchParamsProp} />
      </div>
      <div ref={ref} style={{
      height: "10px"
    }} />
    </>;
}